import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const QueueContext = createContext(null);

const CHUNK_SIZE = 100 * 1024 * 1024; // 50MB
const PARALLEL_CHUNKS = 3;
const MAX_CHUNK_RETRIES = 3;
const CHUNK_TIMEOUT = 600000;

export const formatBytes = (b) => {
  if (!b || b === 0) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatSpeed = (bps) => {
  if (!bps || bps < 500) return '';
  if (bps < 1024 * 1024) return (bps / 1024).toFixed(1) + ' KB/s';
  return (bps / (1024 * 1024)).toFixed(1) + ' MB/s';
};

export const formatETA = (sec) => {
  if (!sec || !isFinite(sec) || sec <= 0 || sec > 86400) return '';
  if (sec < 60) return `${Math.ceil(sec)}s left`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.ceil(sec % 60)}s left`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m left`;
};

export const UploadQueueProvider = ({ children }) => {
  const queueRef = useRef([]);
  const [queueState, setQueueState] = useState([]);
  const processingRef = useRef(false);
  const xhrRefs = useRef({});
  const chunkProgressRef = useRef({});
  const cancelledRef = useRef({});
  const pausedRef = useRef({});   // tracks paused items
  const pauseResolveRef = useRef({}); // resolves when item is resumed

  const syncUI = useCallback(() => {
    setQueueState([...queueRef.current]);
  }, []);

  const updateItem = useCallback((id, updates) => {
    const idx = queueRef.current.findIndex(i => i.id === id);
    if (idx !== -1) {
      queueRef.current[idx] = { ...queueRef.current[idx], ...updates };
      syncUI();
    }
  }, [syncUI]);

  // Tab close protection
  useEffect(() => {
    const handler = (e) => {
      const active = queueRef.current.filter(q => q.status === 'uploading' || q.status === 'waiting' || q.status === 'paused');
      if (active.length > 0) {
        e.preventDefault();
        e.returnValue = 'Files are still uploading. Leave page?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Add files to queue
  const addToQueue = useCallback((files, folderId) => {
    const newItems = files.map(file => ({
      id: `${file.name}_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      name: file.name,
      file,
      folderId,
      status: 'waiting',
      progress: 0,
      speed: 0,
      eta: 0,
      loaded: 0,
      total: file.size,
      fileId: null,
      r2Key: null,
      uploadId: null,
      retryCount: 0,
    }));
    queueRef.current = [...queueRef.current, ...newItems];
    syncUI();
    if (!processingRef.current) processNextInQueue();
  }, [syncUI]);

  // ── Retry a failed item — resets and re-queues it
  const retryItem = useCallback((itemId) => {
    const idx = queueRef.current.findIndex(i => i.id === itemId);
    if (idx === -1) return;
    const item = queueRef.current[idx];

    // Reset item state for retry
    queueRef.current[idx] = {
      ...item,
      status: 'waiting',
      progress: 0,
      speed: 0,
      eta: 0,
      loaded: 0,
      fileId: null,   // fresh init — gets new fileId
      r2Key: null,
      uploadId: null,
      retryCount: (item.retryCount || 0) + 1,
    };
    delete cancelledRef.current[itemId];
    delete pausedRef.current[itemId];
    syncUI();

    if (!processingRef.current) processNextInQueue();
    toast(`Retrying: ${item.name}`, { icon: '🔄', duration: 2000 });
  }, [syncUI]);

  // ── Retry ALL failed items at once
  const retryAllFailed = useCallback(() => {
    const failed = queueRef.current.filter(i => i.status === 'failed');
    failed.forEach(item => {
      const idx = queueRef.current.findIndex(i => i.id === item.id);
      queueRef.current[idx] = {
        ...item,
        status: 'waiting',
        progress: 0,
        speed: 0,
        eta: 0,
        loaded: 0,
        fileId: null,
        r2Key: null,
        uploadId: null,
        retryCount: (item.retryCount || 0) + 1,
      };
      delete cancelledRef.current[item.id];
      delete pausedRef.current[item.id];
    });
    syncUI();
    if (!processingRef.current && failed.length > 0) processNextInQueue();
    toast(`Retrying ${failed.length} failed file${failed.length > 1 ? 's' : ''}`, { icon: '🔄', duration: 2000 });
  }, [syncUI]);

  // ── Pause an uploading item
  const pauseItem = useCallback((itemId) => {
    const item = queueRef.current.find(i => i.id === itemId);
    if (!item || item.status !== 'uploading') return;
    pausedRef.current[itemId] = true;
    updateItem(itemId, { status: 'paused' });
    toast(`Paused: ${item.name}`, { icon: '⏸', duration: 2000 });
  }, [updateItem]);

  // ── Resume a paused item
  const resumeItem = useCallback((itemId) => {
    const item = queueRef.current.find(i => i.id === itemId);
    if (!item || item.status !== 'paused') return;
    delete pausedRef.current[itemId];
    updateItem(itemId, { status: 'waiting' });
    if (!processingRef.current) processNextInQueue();
    toast(`Resumed: ${item.name}`, { icon: '▶', duration: 2000 });
  }, [updateItem]);

  // ── Main queue processor
  const processNextInQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    while (true) {
      const nextItem = queueRef.current.find(i => i.status === 'waiting');
      if (!nextItem) break;

      updateItem(nextItem.id, { status: 'uploading' });

      try {
        await uploadSingleFile(nextItem.id);
        updateItem(nextItem.id, { status: 'done', progress: 100 });
        toast.success(`✓ ${nextItem.name}`, { duration: 2000 });
      } catch (err) {
        if (err.message === 'CANCELLED') {
          updateItem(nextItem.id, { status: 'cancelled' });
        } else if (err.message === 'PAUSED') {
          // stays as 'paused' — will re-enter queue when resumed
        } else {
          console.error(`Upload failed: ${nextItem.name} —`, err.message);
          updateItem(nextItem.id, { status: 'failed' });
          toast.error(`Failed: ${nextItem.name}`, { duration: 4000 });
        }
      }

      delete cancelledRef.current[nextItem.id];
      delete chunkProgressRef.current[nextItem.id];
      delete xhrRefs.current[nextItem.id];
    }

    processingRef.current = false;
  }, [updateItem]);

  // ── Upload single file
  const uploadSingleFile = async (itemId) => {
    const item = queueRef.current.find(i => i.id === itemId);
    if (!item) throw new Error('Item not found');

    const { file, folderId } = item;
    const totalSize = file.size;
    const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);

    // Init multipart
    let initData;
    try {
      const res = await api.post('/files/multipart/init', {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        folder_id: folderId,
        size: totalSize,
      });
      initData = res.data;
    } catch (err) {
      throw new Error(`Init failed: ${err.response?.data?.error || err.message}`);
    }

    const { fileId, uploadId, r2Key } = initData;
    updateItem(itemId, { fileId, uploadId, r2Key });

    chunkProgressRef.current[itemId] = {};
    let speedTimer = Date.now();
    let lastReportedBytes = 0;

    const onChunkProgress = (partNumber, bytesLoaded) => {
      if (!chunkProgressRef.current[itemId]) return;
      chunkProgressRef.current[itemId][partNumber] = bytesLoaded;
      const totalUploaded = Object.values(chunkProgressRef.current[itemId]).reduce((a, b) => a + b, 0);
      const pct = Math.min(Math.round((totalUploaded / totalSize) * 100), 99);
      const now = Date.now();
      const dt = (now - speedTimer) / 1000;
      let speed = 0, eta = 0;
      if (dt >= 0.5) {
        speed = Math.max(0, (totalUploaded - lastReportedBytes) / dt);
        eta = speed > 0 ? (totalSize - totalUploaded) / speed : 0;
        lastReportedBytes = totalUploaded;
        speedTimer = now;
      }
      const idx = queueRef.current.findIndex(i => i.id === itemId);
      if (idx !== -1) {
        queueRef.current[idx] = {
          ...queueRef.current[idx],
          progress: pct,
          speed: speed > 0 ? speed : queueRef.current[idx].speed,
          eta: eta > 0 ? eta : queueRef.current[idx].eta,
          loaded: totalUploaded,
          total: totalSize,
        };
        syncUI();
      }
    };

    const uploadChunk = (presignedUrl, chunk, contentType, partNumber) => {
      return new Promise((resolve, reject) => {
        if (cancelledRef.current[itemId]) { reject(new Error('CANCELLED')); return; }
        if (pausedRef.current[itemId]) { reject(new Error('PAUSED')); return; }

        const xhr = new XMLHttpRequest();
        if (!xhrRefs.current[itemId]) xhrRefs.current[itemId] = {};
        xhrRefs.current[itemId][partNumber] = xhr;

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onChunkProgress(partNumber, e.loaded);
        };
        xhr.onload = () => {
          if (cancelledRef.current[itemId]) { reject(new Error('CANCELLED')); return; }
          if (pausedRef.current[itemId]) { reject(new Error('PAUSED')); return; }
          if (xhr.status === 200) {
            onChunkProgress(partNumber, chunk.size);
            resolve(xhr.getResponseHeader('ETag'));
          } else {
            reject(new Error(`Part ${partNumber} HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error(`Part ${partNumber} network error`));
        xhr.ontimeout = () => reject(new Error(`Part ${partNumber} timeout`));
        xhr.timeout = 600000;
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', contentType || 'application/octet-stream');
        xhr.send(chunk);
      });
    };

    const parts = new Array(totalChunks);

    for (let batchStart = 0; batchStart < totalChunks; batchStart += PARALLEL_CHUNKS) {
      if (cancelledRef.current[itemId]) throw new Error('CANCELLED');
      if (pausedRef.current[itemId]) throw new Error('PAUSED');

      const batchEnd = Math.min(batchStart + PARALLEL_CHUNKS, totalChunks);
      const batchPromises = [];

      for (let i = batchStart; i < batchEnd; i++) {
        const partNumber = i + 1;
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const chunk = file.slice(start, end);

        batchPromises.push((async () => {
  let lastError;
  for (let attempt = 0; attempt <= MAX_CHUNK_RETRIES; attempt++) {
    try {
      const presignRes = await api.post('/files/multipart/presign-part', {
        r2_key: r2Key, upload_id: uploadId, part_number: partNumber,
      });
      const etag = await uploadChunk(
        presignRes.data.presigned_url, chunk,
        file.type || 'application/octet-stream', partNumber
      );
      return { partNumber, etag };
    } catch (err) {
      if (err.message === 'CANCELLED' || err.message === 'PAUSED') throw err;
      lastError = err;
      if (attempt < MAX_CHUNK_RETRIES) {
        console.warn(`[NOVA] Chunk ${partNumber} retry ${attempt + 1}/${MAX_CHUNK_RETRIES}`);
        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
      }
    }
  }
  throw lastError;
})());
      }

      const results = await Promise.all(batchPromises);
      results.forEach(r => { parts[r.partNumber - 1] = r; });
    }

    if (cancelledRef.current[itemId]) throw new Error('CANCELLED');

    const completeRes = await api.post('/files/multipart/complete', {
      file_id: fileId, r2_key: r2Key, upload_id: uploadId,
      parts: parts.filter(Boolean), size: totalSize,
    });
    return completeRes.data;
  };

  // Cancel
  const cancelItem = useCallback(async (itemId) => {
    cancelledRef.current[itemId] = true;
    const xhrs = xhrRefs.current[itemId] || {};
    Object.values(xhrs).forEach(xhr => { try { xhr.abort(); } catch {} });
    const item = queueRef.current.find(i => i.id === itemId);
    if (item?.fileId) {
      api.post('/files/multipart/abort', { file_id: item.fileId, r2_key: item.r2Key, upload_id: item.uploadId }).catch(() => {});
    }
    updateItem(itemId, { status: 'cancelled' });
    toast('Cancelled', { icon: '✕', duration: 2000 });
  }, [updateItem]);

  const removeFromQueue = useCallback((itemId) => {
    queueRef.current = queueRef.current.filter(i => i.id !== itemId);
    syncUI();
  }, [syncUI]);

  const clearCompleted = useCallback(() => {
    queueRef.current = queueRef.current.filter(i => ['waiting','uploading','paused'].includes(i.status));
    syncUI();
  }, [syncUI]);

  const waitingCount = queueState.filter(i => i.status === 'waiting').length;
  const uploadingItem = queueState.find(i => i.status === 'uploading');
  const doneCount = queueState.filter(i => i.status === 'done').length;
  const failedCount = queueState.filter(i => i.status === 'failed').length;

  return (
    <QueueContext.Provider value={{
      queue: queueState,
      isUploading: processingRef.current,
      addToQueue,
      cancelItem,
      retryItem,
      retryAllFailed,
      pauseItem,
      resumeItem,
      removeFromQueue,
      clearCompleted,
      waitingCount,
      uploadingItem,
      doneCount,
      failedCount,
    }}>
      {children}
    </QueueContext.Provider>
  );
};

export const useUploadQueue = () => useContext(QueueContext);